import { Admin, Resource, CustomRoutes, useTranslate } from 'react-admin';
import { MaasLoopLayout } from './layouts/Layout.jsx';
import { maasLoopTheme } from './themes/theme.jsx';
import { GlobalStyles } from './themes/GlobalStyles.jsx';
import { QueryClient } from '@tanstack/react-query';
import { Route, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { MockDataProvider } from './services/mockDataProvider.js';
import { i18nProvider } from './i18n';
import { MaterialList, MaterialEdit } from './pages/material.jsx';
import { ProductList, ProductEdit, ProductCreate } from './pages/product.jsx';
import { TransactionList } from './pages/transaction.jsx';
import { DeviceGroupList } from './pages/deviceGroup.jsx';
import { DeviceGroupShow } from '@/components/deviceGroup/DeviceGroupShow.jsx';
import { ContractorList, ContractorCreate, ContractorEdit } from './pages/contractors.jsx';
import { RetailChainList } from './pages/retailChain.jsx';
import { BrandList } from './pages/brand.jsx';
import { BrandCreate } from '@/components/brand/forms/BrandCreate.jsx';
import { BrandEdit } from '@/components/brand/forms/BrandEdit.jsx';
import { RetailChainCreate } from '@/components/retailChain/forms/RetailChainCreate.jsx';
import { RetailChainEdit } from '@/components/retailChain/forms/RetailChainEdit.jsx';
import { UserList, UserCreate, UserEdit } from './pages/users.jsx';
import { SoftwareList, SoftwareShow, SoftwareCreate, SoftwareEdit } from './pages/software.jsx';
import { Dashboard } from './pages/dashboard.jsx';
import { DeviceList } from './pages/device.jsx';
import { NewDeviceList } from './pages/newDevice.jsx';
import { BagList } from './pages/bag.jsx';
import { TagCategoryList } from './pages/tagCategory.jsx';
import TagCategoryCreate from '@/components/tagCategory/TagCategoryCreate.jsx';
import TagCategoryEdit from '@/components/tagCategory/TagCategoryEdit.jsx';
import { TagList } from './pages/tag.jsx';
import { authProvider } from './services/authProvider.jsx';
import LoginPage from './pages/login.jsx';
import ForgetPasswordPage from './pages/forgetPassword.jsx';
import ResetPasswordPage from './pages/resetPassword.jsx';
import ResetPasswordInfoPage from './pages/resetPasswordInfo.jsx';
import ChangeEmailPage from './pages/changeEmail.jsx';
import { CollectionPointCreate, CollectionPointList } from '@/pages/collectionPoint.jsx';
import { CollectionPointDetailsShow } from '@/components/collectionPoint/CollectionPointDetailsShow.jsx';
import CollectionPointMap from '@/pages/collectionPointMap.jsx';
import { ServiceCompanyCreate, ServiceCompanyEdit, ServiceCompanyList, ServiceCompanyShow } from '@/pages/serviceCompany.jsx';
import { DeviceErrorCreate, DeviceErrorEdit, DeviceErrorList, DeviceErrorShow } from '@/pages/deviceError.jsx';
import { DeviceIssueList } from '@/pages/deviceIssue.jsx';
import { ServiceInspectionList, ServiceInspectionCreate, ServiceInspectionEdit } from '@/pages/serviceInspection.jsx';
import { ServiceRequestCreate, ServiceRequestEdit, ServiceRequestList } from '@/pages/serviceRequest.jsx';
import DeviceErrorReportList from '@/components/device/DeviceErrorReportList.jsx';
import { Dashboard as DAT } from './pages/analyticTransactions.jsx';
import { Dashboard as DAP } from './pages/analyticProducts.jsx';
import { Dashboard as DAS } from './pages/analyticService.jsx';
import DeviceGroupEdit from '@/components/deviceGroup/DeviceGroupEdit.jsx';
import DeviceGroupCreate from '@/components/deviceGroup/DeviceGroupCreate.jsx';
import CollectionPointEdit from '@/components/collectionPoint/CollectionPointEdit.jsx';
import { PromotionalCampaigns } from './pages/promotionalCampaigns.jsx';
import { AdCampaings } from './pages/adsCampaings.jsx';
import { CampaignCreate } from '@/components/promotionalCampaigns/campaign/CampaignCreate.jsx';
import { AdvertisementCreate } from '@/components/adsCampaings/AdvertisementCreate.jsx';
import { AdvertisementEdit } from '@/components/adsCampaings/AdvertisementEdit.jsx';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { VersionChecker } from '@/components/global/menu/VersionChecker.jsx';
import { aliasToString } from '@/helpers/device/softwareHelpers.js';
import { SoftwareAliasCreate, SoftwareAliasEdit, SoftwareAliasList } from '@/pages/softwareAlias.jsx';
import { SoftwareUpdateRuleCreate, SoftwareUpdateRuleEdit, SoftwareUpdateRuleList } from '@/pages/softwareUpdateRule.jsx';
import { AccessKeyCreate, AccessKeyEdit, AccessKeyList } from '@/pages/accessKeys.jsx';
import { PromptList } from '@/pages/prompt.jsx';
import { BillOfMaterialsCreate, BillOfMaterialsEdit, BillOfMaterialsList } from '@/pages/billOfMaterials.jsx';
import { DeviceModelCreate, DeviceModelEdit, DeviceModelList, DeviceModelShow } from '@/pages/deviceModel.jsx';
import { ParameterCreate, ParameterEdit, ParameterList } from '@/pages/parameter.jsx';
import { canViewDeviceConfig } from '@/helpers/device/deviceConfig.js';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            staleTime: 1,
            refetchOnWindowFocus: false,
        },
    },
});

const App = () => {
    const router = createBrowserRouter([
        {
            path: '*',
            element: <CustomAdmin />,
        },
    ]);

    return (
        <HelmetProvider>
            <RouterProvider router={router} />
        </HelmetProvider>
    );
};

const CustomAdmin = () => {
    const translate = useTranslate();
    return (
        <>
            <VersionChecker />
            <Helmet>
                <title>{import.meta.env.VITE_APP_TITLE}</title>
            </Helmet>
            <GlobalStyles />
            <Admin
                dataProvider={MockDataProvider}
                dashboard={Dashboard}
                layout={MaasLoopLayout}
                theme={maasLoopTheme}
                authProvider={authProvider}
                loginPage={LoginPage}
                queryClient={queryClient}
                i18nProvider={i18nProvider}
            >
                <CustomRoutes noLayout>
                    <Route path='/forget-password' element={<ForgetPasswordPage />} disableAuthentication></Route>
                    <Route path='/reset-password/:token' element={<ResetPasswordPage />} disableAuthentication></Route>
                    <Route path='/reset-password-info' element={<ResetPasswordInfoPage />} disableAuthentication></Route>
                    <Route path='/change-email/:token' element={<ChangeEmailPage />} disableAuthentication></Route>
                </CustomRoutes>
                {permissions => (
                    <>
                        {permissions?.isAdmin || permissions?.isClient || permissions?.isBok ? (
                            <Resource
                                name='material'
                                list={MaterialList}
                                edit={permissions.isAdmin ? MaterialEdit : null}
                                recordRepresentation='name'
                                options={{ label: translate('menu.materials') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isClient || permissions?.isBok ? (
                            <Resource
                                name='product'
                                list={ProductList}
                                edit={permissions.isAdmin ? ProductEdit : null}
                                create={permissions.isAdmin ? ProductCreate : null}
                                recordRepresentation='name'
                                options={{ label: translate('menu.products') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isClient || permissions?.isBok ? (
                            <Resource
                                name='transaction'
                                list={TransactionList}
                                recordRepresentation='name'
                                options={{ label: translate('menu.transactions') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isService ? (
                            <Resource
                                name='test-transaction'
                                list={TransactionList}
                                recordRepresentation='id'
                                options={{ label: translate('menu.testTransactionsOverview') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <Resource
                                name='retail-chain'
                                list={RetailChainList}
                                create={permissions?.isAdmin ? RetailChainCreate : null}
                                edit={permissions?.isAdmin ? RetailChainEdit : null}
                                recordRepresentation='name'
                                options={{ label: translate('menu.retailChains') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <Resource
                                name='brand'
                                list={BrandList}
                                create={permissions?.isAdmin ? BrandCreate : null}
                                edit={permissions?.isAdmin ? BrandEdit : null}
                                recordRepresentation='name'
                                options={{ label: translate('menu.brands') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isService || permissions?.isBok || permissions?.isClient ? (
                            <Resource
                                name='contractor'
                                list={(permissions?.isAdmin || permissions?.isBok) && ContractorList}
                                create={permissions?.isAdmin && ContractorCreate}
                                edit={permissions?.isAdmin && ContractorEdit}
                                recordRepresentation='name'
                                options={{ label: translate('menu.contractors') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isClientMaster || permissions?.isServiceMaster || permissions?.isBok ? (
                            <Resource
                                name='user'
                                list={UserList}
                                create={UserCreate}
                                edit={UserEdit}
                                options={{ label: translate('resources.user.name') }}
                                recordRepresentation={record => (record.firstName && record.lastName ? `${record.firstName} ${record.lastName}` : record.email)}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isClient || permissions?.isService || permissions?.isBok ? (
                            <>
                                <Resource
                                    name='collection-point'
                                    options={{ label: translate('menu.locations') }}
                                    list={CollectionPointList}
                                    show={CollectionPointDetailsShow}
                                    create={permissions.isAdmin && CollectionPointCreate}
                                    edit={
                                        (permissions.isAdmin || permissions.isClientMaster || permissions.isBok || permissions.isServiceMaster) &&
                                        CollectionPointEdit
                                    }
                                    recordRepresentation='customId'
                                />
                                <Resource name='simple-collection-point' recordRepresentation='customId' options={{ label: translate('menu.locations') }} />
                            </>
                        ) : null}
                        {permissions?.isAdmin || permissions?.isClient || permissions?.isBok ? (
                            <Resource
                                name='device-group'
                                list={DeviceGroupList}
                                create={permissions.isAdmin ? DeviceGroupCreate : null}
                                show={permissions.isAdmin ? DeviceGroupShow : null}
                                edit={permissions.isAdmin ? DeviceGroupEdit : null}
                                delete={permissions.isAdmin ? DeviceGroupEdit : null}
                                recordRepresentation='name'
                                options={{ label: translate('menu.deviceGroups') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isClient || permissions?.isServiceMaster || permissions?.isBok ? (
                            <Resource name='device' options={{ label: translate('menu.devices') }} list={DeviceList} recordRepresentation='serialNumber' />
                        ) : null}
                        {canViewDeviceConfig(permissions) ? <Resource name='device-config' /> : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <Resource
                                name='new-device'
                                list={NewDeviceList}
                                recordRepresentation='serialNumber'
                                options={{ label: translate('menu.newDevices') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isClient || permissions?.isBok ? (
                            <Resource
                                name='bag'
                                list={BagList}
                                recordRepresentation={record => `${record.seal ?? record.device.serialNumber}`}
                                options={{ label: translate('menu.bags') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isClient || permissions?.isBok ? (
                            <Resource
                                name='tag-category'
                                list={TagCategoryList}
                                create={permissions.isAdmin ? TagCategoryCreate : null}
                                edit={permissions.isAdmin ? TagCategoryEdit : null}
                                recordRepresentation='name'
                                options={{ label: translate('menu.tagCategories') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isClient || permissions?.isBok ? (
                            <Resource name='tag' list={TagList} recordRepresentation='name' options={{ label: translate('menu.tags') }} />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <Resource
                                name='software'
                                list={SoftwareList}
                                create={permissions?.isAdmin && SoftwareCreate}
                                show={SoftwareShow}
                                edit={permissions?.isAdmin && SoftwareEdit}
                                recordRepresentation='version'
                                options={{ label: translate('menu.software') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <Resource
                                name='software-alias'
                                list={SoftwareAliasList}
                                create={permissions?.isAdmin && SoftwareAliasCreate}
                                edit={permissions?.isAdmin && SoftwareAliasEdit}
                                recordRepresentation={aliasToString}
                                options={{ label: translate('menu.softwareAlias') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <Resource
                                name='software-update-rule'
                                list={SoftwareUpdateRuleList}
                                create={permissions?.isAdmin && SoftwareUpdateRuleCreate}
                                edit={permissions?.isAdmin && SoftwareUpdateRuleEdit}
                                recordRepresentation='name'
                                options={{ label: translate('menu.softwareUpdateRule') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isClient || permissions?.isBok ? (
                            <Resource
                                name='service-company'
                                list={(permissions?.isAdmin || permissions?.isBok) && ServiceCompanyList}
                                create={permissions?.isAdmin && ServiceCompanyCreate}
                                show={(permissions?.isAdmin || permissions?.isBok) && ServiceCompanyShow}
                                edit={permissions?.isAdmin && ServiceCompanyEdit}
                                recordRepresentation='name'
                                options={{ label: translate('menu.serviceCompanies') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <Resource
                                name='device-error'
                                list={DeviceErrorList}
                                create={DeviceErrorCreate}
                                show={DeviceErrorShow}
                                edit={DeviceErrorEdit}
                                recordRepresentation='name'
                                options={{ label: translate('menu.errors') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <Resource
                                name='device-model'
                                list={DeviceModelList}
                                create={DeviceModelCreate}
                                show={DeviceModelShow}
                                edit={DeviceModelEdit}
                                recordRepresentation='name'
                                options={{ label: translate('menu.deviceModelManagement') }}
                            />
                        ) : null}
                        {permissions?.isAdmin ? <Resource name='prompt' list={PromptList} options={{ label: translate('menu.promptManagement') }} /> : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <Resource
                                name='parameter'
                                list={ParameterList}
                                create={permissions?.isAdmin ? ParameterCreate : null}
                                edit={permissions?.isAdmin ? ParameterEdit : null}
                                options={{ label: translate('menu.parameterManagement') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isService || permissions?.isBok ? (
                            <Resource
                                name='service-inspection'
                                list={ServiceInspectionList}
                                create={(permissions?.isAdmin || permissions?.isServiceMaster) && ServiceInspectionCreate}
                                edit={(permissions?.isAdmin || permissions?.isServiceMaster) && ServiceInspectionEdit}
                                recordRepresentation='id'
                                options={{ label: translate('menu.serviceInspections') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <>
                                <Resource
                                    name='spare-part'
                                    list={BillOfMaterialsList}
                                    create={permissions?.isAdmin && BillOfMaterialsCreate}
                                    edit={permissions?.isAdmin && BillOfMaterialsEdit}
                                    recordRepresentation='name'
                                    options={{ label: translate('menu.billOfMaterials') }}
                                />
                                <Resource
                                    name='spare-part-module'
                                    recordRepresentation='name'
                                    options={{ label: translate('resources.billOfMaterials.labels.module') }}
                                />
                            </>
                        ) : null}
                        {permissions?.isAdmin || permissions?.isService || permissions?.isBok ? (
                            <Resource
                                name='device-issue'
                                list={DeviceIssueList}
                                recordRepresentation='serialNumber'
                                options={{ label: translate('menu.problemsOverview') }}
                            />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isService || permissions?.isBok ? (
                            <Resource
                                name='service-request'
                                list={ServiceRequestList}
                                create={(permissions?.isAdmin || permissions?.isServiceMaster) && ServiceRequestCreate}
                                edit={(permissions?.isAdmin || permissions?.isServiceMaster) && ServiceRequestEdit}
                                recordRepresentation='customId'
                                options={{ label: translate('menu.serviceRequests') }}
                            />
                        ) : null}
                        {permissions ? <Resource name='device-error-report' list={DeviceErrorReportList} /> : null}
                        {permissions ? <Resource name='notification' /> : null}
                        {permissions?.isAdmin || permissions?.isBok || permissions?.isServiceMaster || permissions?.isClient ? (
                            <Resource name='analytic-transactions' list={DAT} />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok || permissions?.isClient ? <Resource name='analytic-products' list={DAP} /> : null}
                        {permissions?.isAdmin || permissions?.isBok || permissions?.isServiceMaster || permissions?.isClient ? (
                            <Resource name='analytic-service' list={DAS} />
                        ) : null}
                        <Resource name='city' recordRepresentation='name' />
                        {permissions?.isAdmin ||
                        permissions?.isClient ||
                        permissions?.isClientMaster ||
                        permissions?.isPosOwner ||
                        permissions?.isBok ||
                        permissions?.isService ? (
                            <Resource name='collection-point-map' list={CollectionPointMap} />
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok || permissions?.isClient ? (
                            <Resource
                                name='promotional-campaigns'
                                list={PromotionalCampaigns}
                                options={{ label: translate('menu.promotionalCampaigns') }}
                                create={CampaignCreate}
                            />
                        ) : null}
                        {(permissions?.isAdmin || permissions?.isBok || permissions?.isClient || permissions?.isClientMaster) && !permissions?.isPosOwner ? (
                            <Resource
                                name='ad'
                                list={AdCampaings}
                                options={{ label: translate('menu.adsCampaigns') }}
                                create={(permissions?.isAdmin || permissions?.isClient) && !permissions?.isPosOwner ? AdvertisementCreate : null}
                                edit={(permissions?.isAdmin || permissions?.isClient) && !permissions?.isPosOwner ? AdvertisementEdit : null}
                            />
                        ) : null}
                        {permissions?.isAdmin ? (
                            <Resource
                                name='access-key'
                                list={AccessKeyList}
                                create={AccessKeyCreate}
                                edit={AccessKeyEdit}
                                options={{ label: translate('menu.accessKey') }}
                            />
                        ) : null}
                    </>
                )}
            </Admin>
        </>
    );
};

export default App;
